import { extractApiErrorMessage } from './api-error.util';

/**
 * Erro da API normalizado a partir do Problem Details (RFC 9457) que o backend devolve em
 * `api/v1/...` (`application/problem+json`), ainda tolerando os formatos legados que
 * `api-error.util.ts` documenta.
 */
export interface ApiProblem {
  status: number;
  /** Resumo curto vindo do backend; `null` quando não veio nada aproveitável (use o padrão por status). */
  title: string | null;
  /** Mensagem principal, pronta para o usuário; `null` quando o corpo não trouxe texto. */
  detail: string | null;
  /** Todos os erros da operação, sem repetição. Nunca vazio se `detail` existir. */
  errors: string[];
  traceId: string | null;
  instance: string | null;
}

/** Mesmo boilerplate em inglês do ASP.NET tratado em `api-error.util.ts`. */
const ASPNET_VALIDATION_TITLE = 'one or more validation errors occurred.';

function asText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/**
 * Lista de erros do corpo. `errors` varia conforme o caminho no backend: lista de strings
 * (ProblemDetails do GMCore), objeto `{ Campo: string[] }` (ValidationProblemDetails) ou string
 * simples (HttpExceptionFilter em 500). Cai em `messages` (legado) quando `errors` não rende nada.
 */
function collectErrors(record: Record<string, unknown>): string[] {
  const out: string[] = [];
  const push = (value: unknown) => {
    const text = asText(value);
    if (text && !out.includes(text)) out.push(text);
  };

  const errors = record['errors'];
  if (Array.isArray(errors)) errors.forEach(push);
  else if (typeof errors === 'string') push(errors);
  else if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      if (Array.isArray(value)) value.forEach(push);
      else push(value);
    }
  }

  if (!out.length && Array.isArray(record['messages'])) record['messages'].forEach(push);
  return out;
}

function toProblem(status: number, body: unknown): ApiProblem {
  const record: Record<string, unknown> =
    body && typeof body === 'object' && !(body instanceof Blob) ? (body as Record<string, unknown>) : {};

  const errors = collectErrors(record);
  const detail =
    asText(record['detail']) ??
    errors[0] ??
    extractApiErrorMessage({ error: body }) ??
    null;
  if (detail && !errors.includes(detail)) errors.unshift(detail);

  const title = asText(record['title']);

  return {
    status,
    title: title && title.toLowerCase() !== ASPNET_VALIDATION_TITLE ? title : null,
    detail,
    errors,
    traceId: asText(record['traceId']),
    instance: asText(record['instance']),
  };
}

/** Normaliza um `HttpErrorResponse` (ou equivalente com `status`/`error`). Corpo `Blob`: use `resolveApiProblem`. */
export function parseApiProblem(error: { status?: number; error?: unknown }): ApiProblem {
  return toProblem(error.status ?? 0, error.error);
}

/** Como `parseApiProblem`, mas lê o corpo de erro de downloads (`responseType: 'blob'`). */
export async function resolveApiProblem(error: { status?: number; error?: unknown }): Promise<ApiProblem> {
  const status = error.status ?? 0;
  if (!(error.error instanceof Blob)) return toProblem(status, error.error);

  try {
    const text = (await error.error.text()).trim();
    if (!text) return toProblem(status, null);
    try {
      return toProblem(status, JSON.parse(text));
    } catch {
      // Não era JSON: só aproveita se parecer mensagem, e não página de erro do servidor.
      return toProblem(status, text);
    }
  } catch {
    return toProblem(status, null);
  }
}
